import * as React from 'react'
// libs
import { useAuiState, MessagePrimitive } from '@assistant-ui/react'
import { useMutation } from '@apollo/client/react'
import { useConversations } from 'components/Modals/AiChatModal/ConversationsContext'
// icons
import { VerginaSunIcon } from 'components/UI/Icons'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
// context
import { MessageMetadataContext } from 'components/Modals/AiChatModal/ChatRuntimeProvider'
// graphql
import { RISK_CREATE_MUTATION } from 'graphql/risk/mutations'
// types
import { IAiRiskSuggestion, IRiskResponse } from 'types/graphql'
// components
import Markdown from 'components/Modals/AiChatModal/components/Markdown'
// views
import {
  DotsLoader,
  RecordLink,
  AssistantNick,
  AssistantBubble,
  AssistantAvatar,
  AssistantContent,
  ShowMoreButton,
  RecordsContainer,
  SuggestionButton,
  AssistantMessageWrapper
} from './views'

type RiskCreateResponse = { riskCreate: IRiskResponse }

const VISIBLE_RECORDS_LIMIT = 15

const AssistantMessage: React.FC = () => {
  const status = useAuiState((s: any) => s.message.status)
  const messageId = useAuiState((s: any) => s.message.id)

  const { records: allRecords, riskSuggestions: allRiskSuggestions } = React.useContext(MessageMetadataContext)
  const { riskTemplateId } = useConversations()

  const [createRisk] = useMutation<RiskCreateResponse>(RISK_CREATE_MUTATION)

  const isRunning = status.type === 'running'
  const records = allRecords[messageId] ?? []
  const riskSuggestions = allRiskSuggestions[messageId] ?? []

  const [loadingSuggestions, setLoadingSuggestions] = React.useState<Set<string>>(new Set())
  const [isAllRecordsShown, setIsAllRecordsShown] = React.useState(false)

  const visibleRecords = isAllRecordsShown ? records : records.slice(0, VISIBLE_RECORDS_LIMIT)
  const hiddenRecordsCount = records.length - visibleRecords.length

  const handleShowMoreRecords = React.useCallback(() => setIsAllRecordsShown(true), [])

  const handleApplySuggestion = React.useCallback(
    (suggestion: IAiRiskSuggestion) => async () => {
      if (loadingSuggestions.has(suggestion.title) || !riskTemplateId) {
        return
      }

      setLoadingSuggestions((prev) => new Set(prev).add(suggestion.title))

      try {
        const {
          data: {
            riskCreate: { data }
          }
        } = await createRisk({
          variables: {
            input: {
              riskTemplateId,
              fieldValues: suggestion.fields.map(({ value, fieldId }) => ({
                fieldId,
                value
              }))
            }
          }
        })

        window.open(`/forms/edit/${data.id}/template/${riskTemplateId}`, '_blank')
      } finally {
        setLoadingSuggestions((prev) => {
          const next = new Set(prev)
          next.delete(suggestion.title)

          return next
        })
      }
    },
    [loadingSuggestions, riskTemplateId, createRisk]
  )

  return (
    <AssistantMessageWrapper>
      <AssistantAvatar>
        <VerginaSunIcon sx={{ fontSize: 30 }} />
      </AssistantAvatar>
      <AssistantContent>
        <AssistantNick>rAI</AssistantNick>
        <AssistantBubble>
          {isRunning ? (
            <DotsLoader>
              <span />
              <span />
              <span />
            </DotsLoader>
          ) : (
            <MessagePrimitive.Parts
              components={{
                Text: ({ text }: { text: string }) => <Markdown>{text}</Markdown>
              }}
            />
          )}
        </AssistantBubble>

        {!isRunning && records.length > 0 && (
          <RecordsContainer>
            {visibleRecords.map((record) => (
              <RecordLink
                key={record.riskId}
                href={`/forms/edit/${record.riskId}/template/${record.templateId}`}
                target='_blank'
                rel='noopener noreferrer'>
                <OpenInNewIcon sx={{ fontSize: 12 }} />
                <span>{record.title ?? `Risk ${record.riskId}`}</span>
              </RecordLink>
            ))}
            {hiddenRecordsCount > 0 && (
              <ShowMoreButton onClick={handleShowMoreRecords}>Show more ({hiddenRecordsCount})</ShowMoreButton>
            )}
          </RecordsContainer>
        )}

        {!isRunning && riskSuggestions.length > 0 && (
          <RecordsContainer>
            {riskSuggestions.map((suggestion) => {
              const isLoading = loadingSuggestions.has(suggestion.title)

              return (
                <SuggestionButton
                  key={suggestion.title}
                  onClick={handleApplySuggestion(suggestion)}
                  disabled={isLoading}>
                  <AddCircleOutlineIcon sx={{ fontSize: 13 }} />
                  <span>{suggestion.title}</span>
                </SuggestionButton>
              )
            })}
          </RecordsContainer>
        )}
      </AssistantContent>
    </AssistantMessageWrapper>
  )
}

export default AssistantMessage
